import * as React from "react";
import { Check, GalleryHorizontalEnd, Search } from "lucide-react";
import { Dialog, Input, Skeleton, Spinner, Tabs } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ASPECT_CLASS } from "@/containers/Coach/PromptStudio/components/PackageCard";
import { DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";

export interface LookGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every look the post may use, in the row's order. */
  designs: DesignPackageSummary[];
  loading: boolean;
  selectedId: number | null;
  /** Picking a look closes the gallery; the composer renders it from there. */
  onSelect: (id: number) => void;
  /** Offers the image-count filter and marks multi-image looks; off where every look is a carousel. */
  showMultiImageHint?: boolean;
  /** More pages exist beyond `designs`; the gallery asks for them one at a time while open. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

type ImageFilter = "all" | "single" | "several";

const FILTER_LABELS: Record<ImageFilter, string> = { all: "All", single: "Single image", several: "Several images" };

/** Looks whose name contains the search, in their given order. */
export function searchLooks(
  designs: DesignPackageSummary[],
  query: string,
  filter: ImageFilter
): DesignPackageSummary[] {
  const needle = query.trim().toLowerCase();
  return designs.filter(design => {
    if (filter === "single" && design.slidesCount > 1) return false;
    if (filter === "several" && design.slidesCount <= 1) return false;
    return needle.length === 0 || design.name.toLowerCase().includes(needle);
  });
}

/**
 * Every look at a size that can be told apart, with its name, a search box, and a filter by how
 * many images it posts. Browsing renders nothing; only picking a look does.
 */
export const LookGalleryDialog: React.FC<LookGalleryDialogProps> = ({
  open,
  onOpenChange,
  designs,
  loading,
  selectedId,
  onSelect,
  showMultiImageHint = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  const [query, setQuery] = React.useState("");
  const [filter, setFilter] = React.useState<ImageFilter>("all");

  // The rest of the catalog streams in page by page while the gallery is open, so a search sees
  // everything without anyone scrolling for it
  React.useEffect(() => {
    if (open && hasMore && !isLoadingMore && onLoadMore) onLoadMore();
  }, [open, hasMore, isLoadingMore, onLoadMore]);
  const loadingRest = hasMore || isLoadingMore;

  // Each opening starts from the whole catalog
  React.useEffect(() => {
    if (open) {
      setQuery("");
      setFilter("all");
    }
  }, [open]);

  const hasBothKinds =
    showMultiImageHint && designs.some(d => d.slidesCount > 1) && designs.some(d => d.slidesCount <= 1);
  const results = React.useMemo(() => searchLooks(designs, query, filter), [designs, query, filter]);

  const pick = (id: number) => {
    onOpenChange(false);
    onSelect(id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl" showCloseButton>
      <Dialog.Header className="gap-3">
        <Dialog.Title className="text-lg font-bold font-sans">Pick a look</Dialog.Title>
        <div className="flex flex-wrap items-center gap-3 pr-8">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search looks"
              aria-label="Search looks"
              className="pl-8"
              autoFocus
            />
          </div>
          {hasBothKinds && (
            <Tabs value={filter} onValueChange={value => setFilter(value as ImageFilter)}>
              <Tabs.List aria-label="Images per post">
                {(Object.keys(FILTER_LABELS) as ImageFilter[]).map(value => (
                  <Tabs.Trigger key={value} value={value}>
                    {FILTER_LABELS[value]}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
            </Tabs>
          )}
        </div>
      </Dialog.Header>
      <Dialog.Content className="max-h-[min(70dvh,720px)] overflow-y-auto">
        {loading ? (
          <div className="columns-2 gap-4 sm:columns-3 lg:columns-4">
            {[
              "aspect-square",
              "aspect-[4/5]",
              "aspect-[9/16]",
              "aspect-square",
              "aspect-[4/5]",
              "aspect-square",
              "aspect-[9/16]",
              "aspect-[4/5]",
            ].map((aspect, i) => (
              <Skeleton key={i} className={cn("mb-4 w-full break-inside-avoid rounded-xl", aspect)} />
            ))}
          </div>
        ) : results.length === 0 && !loadingRest ? (
          <p className="py-10 text-center text-sm text-slate-500" data-testid="look-gallery-empty">
            No looks match.
          </p>
        ) : (
          // Columns pack cards of different shapes tightly: each keeps its design's real ratio and
          // the next card tucks under whichever is shorter
          <div
            role="radiogroup"
            aria-label="All looks"
            className="columns-2 gap-4 sm:columns-3 lg:columns-4"
            data-testid="look-gallery"
          >
            {results.map(design => {
              const selected = design.id === selectedId;
              const multiImage = showMultiImageHint && design.slidesCount > 1;
              return (
                <button
                  key={design.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={multiImage ? `${design.name}, several images` : design.name}
                  onClick={() => pick(design.id)}
                  className={cn(
                    "group relative mb-4 flex w-full break-inside-avoid flex-col overflow-hidden rounded-xl border bg-white text-left transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                    selected
                      ? "border-transparent ring-2 ring-primary ring-offset-2"
                      : "border-slate-200 hover:border-slate-400"
                  )}
                  data-testid="look-card"
                >
                  <span className={cn("block w-full bg-slate-100", ASPECT_CLASS[design.aspectRatio])}>
                    {design.thumbnailUrl ? (
                      <img src={design.thumbnailUrl} alt="" className="size-full object-cover" />
                    ) : (
                      <span className="block size-full bg-slate-200" />
                    )}
                  </span>
                  {/* The name belongs to the card, so it sits inside its border under the image */}
                  <span className="flex min-w-0 items-start gap-2 border-t border-slate-100 px-2.5 py-2 leading-tight">
                    <span className="line-clamp-2 min-w-0 flex-1 text-xs font-semibold text-slate-800">
                      {design.name}
                    </span>
                    {multiImage && (
                      <span className="flex shrink-0 items-center gap-1 text-xs text-slate-500">
                        <GalleryHorizontalEnd className="size-3" aria-hidden="true" />
                        {design.slidesCount} images
                      </span>
                    )}
                  </span>
                  {selected && (
                    <span className="absolute top-2 right-2 flex size-5 items-center justify-center rounded-full bg-primary text-white shadow">
                      <Check className="size-3" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {!loading && loadingRest && (
          <p
            className="flex items-center justify-center gap-2 py-3 text-xs text-slate-500"
            data-testid="look-gallery-loading-more"
          >
            <Spinner className="size-3" /> Loading more looks…
          </p>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
